import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, PlusCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  getFeedback,
  createFeedback,
  updateFeedback,
  type FeedbackRecord,
  type FeedbackType,
} from '../services/feedbackService';
import { aikupData } from '../data/aikupData';

type ContextLevel = 'GENERAL' | 'DEPARTMENT' | 'CARD';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  departmentKey: string;
  contextLevel: ContextLevel;
  cardId?: number | null;
  contextLabel: string;
  leaderEmail?: string; // Pour contrôler le droit de création
}

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Basse',
  2: 'Moyenne',
  3: 'Haute',
  4: 'Critique',
};

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'BUG', label: 'Bug / anomalie' },
  { value: 'CONTENU', label: 'Contenu' },
  { value: 'SUGGESTION_CONCEPT', label: 'Suggestion de concept' },
  { value: 'LANGUE', label: 'Langue / coquille' },
  { value: 'UX', label: 'UX / expérience' },
  { value: 'BRAINSTORM', label: 'Brainstorm / idée brute' },
  { value: 'PROCESSUS_METHODOLOGIE', label: 'Processus / méthodologie' },
  { value: 'AUTRE', label: 'Autre' },
];

export default function FeedbackModal({
  isOpen,
  onClose,
  departmentKey,
  contextLevel,
  cardId = null,
  contextLabel,
  leaderEmail,
}: FeedbackModalProps) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [allFeedbacks, setAllFeedbacks] = useState<FeedbackRecord[]>([]);
  const [cardFeedbacks, setCardFeedbacks] = useState<FeedbackRecord[]>([]);
  const [activeScope, setActiveScope] = useState<'CARD' | 'DEPARTMENT' | 'ALL'>(
    contextLevel === 'CARD' ? 'CARD' : 'DEPARTMENT'
  );
  const [editing, setEditing] = useState<FeedbackRecord | null>(null);

  const [type, setType] = useState<FeedbackType>('AUTRE');
  const [priority, setPriority] = useState<number>(2);
  const [expectedDate, setExpectedDate] = useState<string>('');
  const [promisedDate, setPromisedDate] = useState<string>('');
  const [comment, setComment] = useState<string>('');

  const currentEmail = user?.email?.toLowerCase() || '';
  const isDany = currentEmail === 'dany.bourassa@bzinc.ca';
  const isOwnerLeader = leaderEmail ? currentEmail === leaderEmail.toLowerCase() : false;

  const canCreateOrEdit = isDany || isOwnerLeader;

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        setIsLoading(true);
        if (contextLevel === 'CARD' && cardId != null) {
          // Contexte carte : uniquement les commentaires de cette carte
          const cardData = await getFeedback(departmentKey, cardId);
          setCardFeedbacks(cardData);
          setAllFeedbacks([]);
        } else {
          // Contexte département/général : commentaires généraux + département + cartes
          const deptAndCards = await getFeedback(departmentKey, undefined, { includeCards: true });
          setAllFeedbacks(deptAndCards);
          setCardFeedbacks(
            deptAndCards.filter((f) => f.context_level === 'CARD' && f.card_id != null)
          );
        }
      } catch (error) {
        console.error('Erreur chargement du feedback:', error);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [isOpen, departmentKey, contextLevel, cardId]);

  const resetForm = () => {
    setEditing(null);
    setType('AUTRE');
    setPriority(2);
    setExpectedDate('');
    setPromisedDate('');
    setComment('');
  };

  const handleEditClick = (fb: FeedbackRecord) => {
    // Un utilisateur ne peut éditer que ses propres feedbacks, sauf Dany
    if (!isDany && fb.user_email.toLowerCase() !== currentEmail) return;
    setEditing(fb);
    setType(fb.type);
    setPriority(fb.priority);
    setExpectedDate(fb.expected_date || '');
    setPromisedDate(fb.promised_date || '');
    setComment(fb.comment);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateOrEdit) return;
    if (!comment.trim()) return;

    try {
      setIsLoading(true);
      if (editing) {
        const updated = await updateFeedback(editing.id, {
          type,
          priority,
          expected_date: expectedDate || null,
          // promised_date ne peut être modifiée que par Dany
          ...(isDany && promisedDate ? { promised_date: promisedDate } : {}),
          comment,
          // gestionnaire peut cocher son approbation
          approved_manager:
            editing.user_email.toLowerCase() === currentEmail ? true : editing.approved_manager,
          department_key: departmentKey,
          card_id: contextLevel === 'CARD' ? cardId ?? null : null,
        });
        const isCardScope = updated.context_level === 'CARD' && updated.card_id != null;
        if (isCardScope) {
          setCardFeedbacks((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
        } else {
          setAllFeedbacks((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
        }
      } else {
        const created = await createFeedback({
          context_level: contextLevel,
          department_key: departmentKey,
          card_id: contextLevel === 'CARD' ? cardId ?? null : null,
          type,
          priority,
          expected_date: expectedDate || null,
          comment,
          approved_manager: true, // le gestionnaire qui crée se considère approuvant son commentaire
        });
        const isCardScope = created.context_level === 'CARD' && created.card_id != null;
        if (isCardScope) {
          setCardFeedbacks((prev) => [created, ...prev]);
        } else {
          setAllFeedbacks((prev) => [created, ...prev]);
        }
      }
      resetForm();
    } catch (error) {
      console.error('Erreur sauvegarde feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const feedbacksToShow: FeedbackRecord[] = (() => {
    // Si on est dans une carte, on affiche uniquement les commentaires de cette carte
    if (contextLevel === 'CARD') {
      return cardFeedbacks.slice().sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
    }

    if (activeScope === 'CARD') {
      return cardFeedbacks.slice().sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
    }
    if (activeScope === 'DEPARTMENT') {
      return allFeedbacks
        .filter((f) => f.context_level === 'GENERAL' || f.context_level === 'DEPARTMENT')
        .sort((a, b) => {
          const da = a.created_at ? new Date(a.created_at).getTime() : 0;
          const db = b.created_at ? new Date(b.created_at).getTime() : 0;
          return db - da;
        });
    }
    // ALL: concat avec élimination des doublons éventuels
    const byId = new Map<number, FeedbackRecord>();
    allFeedbacks.forEach((f) => byId.set(f.id, f));
    cardFeedbacks.forEach((f) => byId.set(f.id, f));
    return Array.from(byId.values()).sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  })();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-200"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-linear-to-r from-bz-blue/15 via-indigo-50 to-purple-50">
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-bz-dark flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-bz-blue" />
                  Commentaires – {contextLabel}
                </h3>
                <p className="text-xs text-gray-500 max-w-2xl">
                  Tous les commentaires sont visibles par l’équipe. Seul l’auteur peut modifier son propre commentaire (sauf Dany qui peut tout ajuster et approuver).
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/70 bg-white/40 border border-gray-200 transition-colors shadow-sm"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Contenu */}
            <div className="flex-1 flex flex-col md:flex-row min-h-0">
              {/* Liste des commentaires */}
              <div className="flex-1 border-r border-gray-200 overflow-y-auto p-4 space-y-3 bg-linear-to-b from-gray-50 to-white">
                {/* Filtres de portée */}
                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                  {contextLevel === 'CARD' ? (
                    <span className="text-[11px] text-gray-600">
                      Portée affichée : commentaires de cette carte uniquement
                    </span>
                  ) : (
                    <div className="flex items-center gap-2 text-[11px] text-gray-500">
                      <span>Portée affichée :</span>
                      <div className="inline-flex rounded-full bg-gray-100 border border-gray-200 p-1">
                        <button
                          type="button"
                          onClick={() => setActiveScope('DEPARTMENT')}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            activeScope === 'DEPARTMENT'
                              ? 'bg-bz-blue text-white shadow-sm'
                              : 'text-gray-600 hover:bg-white'
                          }`}
                        >
                          Département & Général
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveScope('CARD')}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            activeScope === 'CARD'
                              ? 'bg-bz-blue text-white shadow-sm'
                              : 'text-gray-600 hover:bg-white'
                          }`}
                        >
                          Cartes
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveScope('ALL')}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            activeScope === 'ALL'
                              ? 'bg-bz-blue text-white shadow-sm'
                              : 'text-gray-600 hover:bg-white'
                          }`}
                        >
                          Tous
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] text-gray-500 ml-auto">
                    {feedbacksToShow.length} commentaire(s)
                  </div>
                </div>

                {isLoading && feedbacksToShow.length === 0 && (
                  <p className="text-sm text-gray-500">Chargement des commentaires…</p>
                )}
                {!isLoading && feedbacksToShow.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Aucun commentaire pour l’instant. Ajoutez le premier pour ce contexte.
                  </p>
                )}
                {feedbacksToShow.map((fb) => {
                  const emailLower = fb.user_email?.toLowerCase() || '';
                  const avatarMap: Record<string, string> = {
                    'dany.bourassa@bzinc.ca': '/DB.png',
                    'jean-pierre.leclerc@bzinc.ca': '/JP.png',
                    'eric.hains@bzinc.ca': '/EH.png',
                    'vincent.bourassa@bzinc.ca': '/VB.png',
                    'jean-michel.caouette@bzinc.ca': '/JMC.png',
                    'francois.delisle@bzinc.ca': '/FP.png',
                  };
                  const avatarSrc = avatarMap[emailLower];
                  const isCardContext = fb.context_level === 'CARD' && fb.card_id != null;
                  const cardTitle = isCardContext
                    ? aikupData.find((c) => c.id === fb.card_id)?.title
                    : undefined;

                  const containerClasses =
                    fb.context_level === 'CARD'
                      ? 'bg-sky-50 border-sky-200'
                      : fb.context_level === 'DEPARTMENT'
                      ? 'bg-indigo-50/60 border-indigo-200'
                      : 'bg-slate-50 border-slate-200';

                  return (
                    <div
                      key={fb.id}
                      className={`border rounded-xl p-3 flex flex-col gap-1 ${containerClasses}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-700">
                          {avatarSrc && (
                            <div className="w-7 h-7 rounded-full overflow-hidden border border-gray-200">
                              <img
                                src={avatarSrc}
                                alt={fb.user_email}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex flex-col">
                            <span className="font-semibold">{fb.user_email}</span>
                            <span className="text-[11px] text-gray-500">
                              {fb.created_at
                                ? new Date(fb.created_at).toLocaleString()
                                : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          {isCardContext && cardTitle && (
                            <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                              Carte : {cardTitle}
                            </span>
                          )}
                          <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                            {FEEDBACK_TYPES.find((t) => t.value === fb.type)?.label || fb.type}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                            {PRIORITY_LABELS[fb.priority] || `Priorité ${fb.priority}`}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{fb.comment}</p>
                      <div className="flex items-center justify-between mt-1 text-[11px] text-gray-500">
                        <div className="flex items-center gap-2">
                          {fb.expected_date && (
                            <span>Souhaité: {new Date(fb.expected_date).toLocaleDateString()}</span>
                          )}
                          {fb.promised_date && (
                            <span>Promis: {new Date(fb.promised_date).toLocaleDateString()}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {fb.approved_manager && <span>✔ Gestionnaire</span>}
                          {fb.approved_ce && <span>✔ CE</span>}
                          {fb.approved_dany && <span>✔ Dany</span>}
                          {(isDany || fb.user_email.toLowerCase() === currentEmail) && (
                            <button
                              className="text-bz-blue hover:underline"
                              onClick={() => handleEditClick(fb)}
                            >
                              Modifier
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Formulaire d’ajout / édition */}
              <div className="w-full md:w-[380px] p-5 bg-gray-50 border-t md:border-t-0 md:border-l border-gray-200 shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-bz-dark">
                    {editing ? 'Modifier le commentaire' : 'Nouveau commentaire'}
                  </h4>
                  {canCreateOrEdit && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex items-center gap-1 text-xs text-bz-blue hover:underline"
                    >
                      <PlusCircle className="w-3 h-3" />
                      Nouveau
                    </button>
                  )}
                </div>

                {!canCreateOrEdit && (
                  <p className="text-xs text-gray-500 mb-3">
                    Vous pouvez consulter les commentaires, mais seuls le gestionnaire concerné et Dany peuvent en ajouter ou modifier.
                  </p>
                )}

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Type</label>
                    <select
                      className="w-full border rounded-md px-2 py-1 text-sm"
                      value={type}
                      onChange={(e) => setType(e.target.value as FeedbackType)}
                      disabled={!canCreateOrEdit}
                    >
                      {FEEDBACK_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Priorité</label>
                    <select
                      className="w-full border rounded-md px-2 py-1 text-sm"
                      value={priority}
                      onChange={(e) => setPriority(Number(e.target.value))}
                      disabled={!canCreateOrEdit}
                    >
                      {[1, 2, 3, 4].map((p) => (
                        <option key={p} value={p}>
                          {p} – {PRIORITY_LABELS[p]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Date souhaitée</label>
                    <input
                      type="date"
                      className="w-full border rounded-md px-2 py-1 text-sm"
                      value={expectedDate}
                      onChange={(e) => setExpectedDate(e.target.value)}
                      disabled={!canCreateOrEdit}
                    />
                  </div>

                  {isDany && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Date promise (Dany)</label>
                      <input
                        type="date"
                        className="w-full border rounded-md px-2 py-1 text-sm"
                        value={promisedDate}
                        onChange={(e) => setPromisedDate(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Commentaire</label>
                    <textarea
                      className="w-full border rounded-md px-2 py-1 text-sm min-h-[80px]"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      disabled={!canCreateOrEdit}
                    />
                  </div>

                  {canCreateOrEdit && (
                    <button
                      type="submit"
                      disabled={isLoading || !comment.trim()}
                      className="w-full mt-2 px-4 py-2 rounded-md bg-bz-blue text-white text-sm font-semibold hover:bg-bz-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {editing ? 'Mettre à jour' : 'Ajouter le commentaire'}
                    </button>
                  )}
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

